// src/modules/app-views/crm/pages/Boards/components/StickyNoteModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Form, Input, Modal, Select } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const STICKY_COLORS = [
  '#fff9c4', // yellow
  '#f8bbd0', // pink
  '#bbdefb', // blue
  '#c8e6c9', // green
  '#e1bee7', // purple
  '#ffe0b2', // orange
  '#d7ccc8', // brown
  '#cfd8dc', // grey
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const StickyNoteModal = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { show, setShow, note, onSave } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!note;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (show && note) {
      form.setFieldsValue({
        title: note.title || '',
        content: note.content || '',
        color: note.color || STICKY_COLORS[0],
        tags: note.tags || [],
      });
    } else if (show) {
      form.resetFields();
      form.setFieldsValue({ color: STICKY_COLORS[0] });
    }
  }, [show, note, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave({
        ...values,
        _id: note?._id || note?.uid || null,
      });
      setShow(false);
    } catch {
      // validation error
    }
  };

  const handleCancel = () => {
    setShow(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={show}
      title={translateX(isEditing ? 'edit_note' : 'new_note')}
      okText={translateX('save')}
      cancelText={translateX('cancel')}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnHidden
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="title"
          label={translateX('title')}
          rules={[{ required: true, message: () => translateX('required_field') }]}
        >
          <Input placeholder={translateX('title')} maxLength={100} />
        </Form.Item>

        <Form.Item name="content" label={translateX('description')}>
          <Input.TextArea
            rows={5}
            placeholder={translateX('description')}
            maxLength={1000}
            showCount
          />
        </Form.Item>

        <Form.Item name="color" label={translateX('color')}>
          <ColorPalette colors={STICKY_COLORS} />
        </Form.Item>

        <Form.Item name="tags" label={translateX('tags')}>
          <Select
            mode="tags"
            placeholder={translateX('tags')}
            style={{ width: '100%' }}
            tokenSeparators={[',']}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default StickyNoteModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function ColorPalette({ value, onChange, colors }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {colors.map(color => (
        <div
          key={color}
          onClick={() => onChange(color)}
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: color,
            cursor: 'pointer',
            border: value === color ? '2px solid #1890ff' : '2px solid transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            transition: 'border-color 0.2s',
          }}
        >
          {value === color && <CheckOutlined style={{ fontSize: 14, color: '#1890ff' }} />}
        </div>
      ))}
    </div>
  );
}
